import React from 'react';
import { Typography, Card, Row, Col, Select, Slider, Button } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';

const { Title, Text } = Typography;

// Tamaño por defecto del título del hero (px). Unifica todas las páginas.
const HERO_SIZE_DEFAULT = 32;

// Combinaciones de tipografías de Google Fonts
const FONT_COMBOS = {
  clasica: {
    nombre: 'Clásica',
    descripcion: 'Elegante y tradicional, ideal para cabañas de renombre',
    titulo: 'Playfair Display',
    cuerpo: 'Lato',
    googleUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lato:wght@300;400;700&display=swap'
  },
  moderna: {
    nombre: 'Moderna',
    descripcion: 'Limpia y profesional, transmite confianza',
    titulo: 'Montserrat',
    cuerpo: 'Open Sans',
    googleUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Open+Sans:wght@300;400;600&display=swap'
  },
  dinamica: {
    nombre: 'Dinámica',
    descripcion: 'Moderna y versátil, perfecta para un look fresco',
    titulo: 'Poppins',
    cuerpo: 'Roboto',
    googleUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@300;400;500&display=swap'
  }
};

// A NIVEL DE MODULO, igual que `ColorField` en ColoresTab e `ImageOption` en
// EstructuraTab. Vivia DENTRO del render de `TipografiasTab`, y este tab tiene
// un Slider de tamaño de titulo: cada arrastre re-renderiza el padre, React ve
// una funcion `FontComboCard` NUEVA y desmonta y remonta las tres tarjetas. Y
// adentro de cada una hay un <link> de Google Fonts, asi que las previews
// parpadeaban re-inyectando la hoja de estilos en cada pixel del slider.
//
// `onChange` pasa a venir por props: afuera del padre ya no esta en el scope.
const FontComboCard = ({ comboKey, combo, isSelected, onChange }) => (
  <Card
    hoverable
    onClick={() => onChange('tipografia_combo', comboKey)}
    style={{
      border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
      backgroundColor: isSelected ? '#e6f7ff' : '#fff',
      cursor: 'pointer',
      position: 'relative',
      marginBottom: 16
    }}
    styles={{ body: { padding: 16 } }}
  >
    {isSelected && (
      <CheckCircleFilled 
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          fontSize: 20,
          color: '#1890ff'
        }}
      />
    )}
    
    {/* Cargar fuente para preview */}
    <link href={combo.googleUrl} rel="stylesheet" />
    
    <div style={{ marginBottom: 12 }}>
      <Text strong style={{ fontSize: 14, color: '#1890ff' }}>
        {combo.nombre}
      </Text>
      <br />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {combo.descripcion}
      </Text>
    </div>

    {/* Preview de la combinación */}
    <div style={{ 
      backgroundColor: '#fafafa', 
      padding: 12, 
      borderRadius: 6,
      marginTop: 8 
    }}>
      <div style={{ 
        fontFamily: `'${combo.titulo}', serif`,
        fontSize: 20,
        fontWeight: 600,
        marginBottom: 4,
        color: '#1a1a1a'
      }}>
        Título Principal
      </div>
      <div style={{ 
        fontFamily: `'${combo.titulo}', serif`,
        fontSize: 14,
        fontWeight: 500,
        marginBottom: 8,
        color: '#333'
      }}>
        Subtítulo de sección
      </div>
      <div style={{ 
        fontFamily: `'${combo.cuerpo}', sans-serif`,
        fontSize: 13,
        lineHeight: 1.5,
        color: '#666'
      }}>
        Este es un ejemplo de texto de párrafo para mostrar cómo se verá el contenido en tu catálogo digital.
      </div>
    </div>

    {/* Info de fuentes */}
    <div style={{ marginTop: 12 }}>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>Títulos:</Text>
          <br />
          <Text style={{ fontSize: 12, fontWeight: 500 }}>{combo.titulo}</Text>
        </Col>
        <Col xs={24} sm={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>Cuerpo:</Text>
          <br />
          <Text style={{ fontSize: 12, fontWeight: 500 }}>{combo.cuerpo}</Text>
        </Col>
      </Row>
    </div>
  </Card>
);

const TipografiasTab = ({ config, onChange }) => {
  

  return (
    <div className="tipografias-tab">
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Combinación de Tipografías</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Selecciona una combinación de fuentes de Google Fonts para tu catálogo
        </Text>
        
        {/* APILADAS, una por fila. Ponerlas de a dos parecia mejor para
            comparar, pero este tab vive dentro de una columna de 8/24
            (Micrositio usa `tabPosition="left"`): media columna de un tercio
            del viewport deja cada preview de tipografia sin ancho para
            mostrar una linea de muestra, que es todo lo que la card tiene
            para decir. */}
        {Object.entries(FONT_COMBOS).map(([key, combo]) => (
          <FontComboCard
            key={key}
            comboKey={key}
            combo={combo}
            isSelected={config.tipografia_combo === key}
            onChange={onChange}
          />
        ))}
      </div>

      {/* Título del Hero — transformación + tamaño */}
      <Card title="Título del Hero" style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Cómo se muestra el título sobre la imagen del hero (catálogo, ficha y páginas internas).
        </Text>
        <Row align="middle" gutter={12} style={{ marginBottom: 20 }}>
          <Col flex="auto">
            <Text strong style={{ display: 'block' }}>Transformación del texto</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>Mayúsculas, minúsculas, etc.</Text>
          </Col>
          <Col>
            <Select
              value={config.text_transform_hero || 'none'}
              onChange={(val) => onChange('text_transform_hero', val)}
              style={{ width: 160 }}
              options={[
                { value: 'none',       label: 'Normal' },
                { value: 'uppercase',  label: 'MAYÚSCULAS' },
                { value: 'lowercase',  label: 'minúsculas' },
                { value: 'capitalize', label: 'Capitalizado' },
              ]}
            />
          </Col>
        </Row>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Tamaño del título</Text>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} sm={18}>
              <Slider
                min={18}
                max={72}
                step={1}
                value={config.hero_text_size || HERO_SIZE_DEFAULT}
                onChange={(v) => onChange('hero_text_size', v)}
              />
            </Col>
            <Col xs={24} sm={6}>
              <Text strong>{config.hero_text_size || HERO_SIZE_DEFAULT} px</Text>
              {config.hero_text_size && (
                <Button type="link" size="small" style={{ padding: '0 0 0 8px' }} onClick={() => onChange('hero_text_size', null)}>
                  Restaurar
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </Card>

      {/* Info adicional */}
      <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Text style={{ fontSize: 12 }}>
          <strong>💡 Tip:</strong> Las tipografías se cargan desde Google Fonts,
          garantizando una carga rápida y compatibilidad con todos los navegadores.
        </Text>
      </Card>
    </div>
  );
};

// Exportar también las combinaciones para usar en otros componentes
export { FONT_COMBOS };
export default TipografiasTab;
